import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { LuBanknote } from 'react-icons/lu';
import * as Yup from 'yup';

import { FrequencyType, TaxableIncome, TaxableIncomeType } from '@ndvr/portfolio-api';

import { FormCheckbox, FormInput, FormSelect } from '@/components/form';
import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { titleCase } from 'utils';
import { convertServerDateToFormDate } from 'utils/date-formatter';

interface EditTaxableIncomeDialogProps {
    income?: TaxableIncome;
    onSave: (data: FormValues) => void;
    onCancel: () => void;
    open: boolean;
    isActive?: boolean;
}

const formSchema = Yup.object({
    title: Yup.string()
        .required('Title is required')
        .trim()
        .min(1, 'Title cannot be empty')
        .max(64, 'Title cannot exceed 64 characters'),
    taxableIncomeType: Yup.string()
        .oneOf(Object.values(TaxableIncomeType), 'Income type is required')
        .required('Income type is required'),
    amount: Yup.number()
        .typeError('Amount must be a number')
        .required('Amount is required')
        .positive('Amount must be positive')
        .min(0.01, 'Amount must be at least $0.01'),
    startDate: Yup.string().required('Start date is required'),
    endDate: Yup.string()
        .optional()
        .test('is-after-start', 'End date must be after start date', function (value) {
            const startDate = this.parent.startDate;
            if (!value || !startDate) return true;
            return new Date(value) > new Date(startDate);
        }),
    repeatIncome: Yup.boolean(),
    frequencyType: Yup.string()
        .oneOf(Object.values(FrequencyType), 'Frequency is required')
        .when('repeatIncome', {
            is: true,
            then: () => Yup.string().required('Frequency is required'),
            otherwise: () => Yup.string().notRequired(),
        }),
    repeatUntil: Yup.string()
        .oneOf(['cancel', 'specific-date', 'occurrences'])
        .when('repeatIncome', {
            is: true,
            then: () => Yup.string().required('Repeat until option is required'),
            otherwise: () => Yup.string().optional(),
        }),
    occurrenceCount: Yup.number()
        .optional()
        .when(['repeatIncome', 'repeatUntil'], {
            is: (repeatIncome: boolean, repeatUntil: string) => repeatIncome && repeatUntil === 'occurrences',
            then: () =>
                Yup.number()
                    .typeError('Number of occurrences must be a number')
                    .required('Number of occurrences is required')
                    .positive('Must be positive')
                    .integer('Must be a whole number'),
            otherwise: () => Yup.number().notRequired(),
        }),
    inflationAdjusted: Yup.boolean(),
    inflationPercent: Yup.number()
        .optional()
        .when('inflationAdjusted', {
            is: true,
            then: () =>
                Yup.number()
                    .required('Inflation rate is required when adjusting for inflation')
                    .min(-99, 'Inflation rate must be greater than -99%')
                    .typeError('Must be a number'),
            otherwise: () => Yup.number().notRequired(),
        }),
    inflationStartDate: Yup.string().optional(),
});

export type FormValues = Yup.InferType<typeof formSchema>;

export function EditTaxableIncomeDialog({ income, onSave, onCancel, open }: EditTaxableIncomeDialogProps) {
    const titleInputRef = useRef<HTMLInputElement | null>(null);

    const getDefaultValues = useCallback((incomeData?: TaxableIncome): FormValues => {
        if (incomeData) {
            const isRepeating = incomeData.factorDateFrame.frequencyType !== FrequencyType.ONE_TIME;
            const hasEndDate = !!incomeData.factorDateFrame.endDate;
            const hasOccurrenceCount = !!incomeData.factorDateFrame.occurrenceCount;

            let repeatUntil: 'cancel' | 'specific-date' | 'occurrences' | undefined;
            if (isRepeating) {
                if (hasOccurrenceCount) {
                    repeatUntil = 'occurrences';
                } else if (hasEndDate) {
                    repeatUntil = 'specific-date';
                } else {
                    repeatUntil = 'cancel';
                }
            }

            return {
                title: incomeData.title.plainText,
                taxableIncomeType: incomeData.taxableIncomeType,
                amount: Number(incomeData.amount),
                startDate: incomeData.factorDateFrame.startDate
                    ? convertServerDateToFormDate(incomeData.factorDateFrame.startDate)
                    : '',
                endDate: incomeData.factorDateFrame.endDate
                    ? convertServerDateToFormDate(incomeData.factorDateFrame.endDate)
                    : undefined,
                repeatIncome: isRepeating,
                frequencyType: incomeData.factorDateFrame.frequencyType,
                repeatUntil,
                occurrenceCount: incomeData.factorDateFrame.occurrenceCount,
                inflationAdjusted: incomeData.inflationConfig.inflationAdjusted,
                inflationPercent: incomeData.inflationConfig.inflationPercent
                    ? Number(incomeData.inflationConfig.inflationPercent)
                    : undefined,
                inflationStartDate: incomeData.inflationConfig.inflationStartDate
                    ? convertServerDateToFormDate(incomeData.inflationConfig.inflationStartDate)
                    : undefined,
            };
        }

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];

        return {
            title: '',
            taxableIncomeType: TaxableIncomeType.SALARY,
            amount: 0,
            startDate: tomorrowStr,
            endDate: undefined,
            repeatIncome: false,
            frequencyType: FrequencyType.ONE_TIME,
            repeatUntil: undefined,
            occurrenceCount: undefined,
            inflationAdjusted: false,
            inflationPercent: undefined,
            inflationStartDate: undefined,
        };
    }, []);

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(income),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(income));
    }, [income, form, getDefaultValues]);

    useEffect(() => {
        const timer = setTimeout(() => {
            titleInputRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleSave = (data: FormValues) => {
        onSave(data);
    };

    const inflationAdjusted = form.watch('inflationAdjusted');
    const repeatIncome = form.watch('repeatIncome');
    const repeatUntil = form.watch('repeatUntil');

    const handleRepeatIncomeChange = (checked: boolean) => {
        form.setValue('repeatIncome', checked);
        if (checked) {
            if (!form.getValues('frequencyType') || form.getValues('frequencyType') === FrequencyType.ONE_TIME) {
                form.setValue('frequencyType', FrequencyType.MONTHLY);
            }
            if (!form.getValues('repeatUntil')) {
                form.setValue('repeatUntil', 'cancel');
            }
        }
    };

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isAdding = !income;
    const modalTitle = isAdding ? 'Add Taxable Income' : 'Edit Taxable Income';

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LuBanknote className="h-5 w-5 text-gray-600" />
                        {modalTitle}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 px-1">
                    <Form {...form}>
                        <form id="taxable-income-form" onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                            <div className="space-y-3">
                                <FormInput
                                    control={form.control}
                                    name="title"
                                    label="Title"
                                    placeholder="Enter income title"
                                    inputRef={titleInputRef}
                                />

                                <FormSelect
                                    control={form.control}
                                    name="taxableIncomeType"
                                    label="Income Type"
                                    placeholder="Select income type"
                                >
                                    {Object.values(TaxableIncomeType).map((type) => (
                                        <SelectItem key={type} value={type}>
                                            {titleCase(type)}
                                        </SelectItem>
                                    ))}
                                </FormSelect>

                                <FormInput
                                    control={form.control}
                                    name="amount"
                                    label="Amount ($)"
                                    placeholder="Enter amount"
                                >
                                    {(field) => (
                                        <NumberInput
                                            placeholder="Enter amount"
                                            {...field}
                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                        />
                                    )}
                                </FormInput>
                            </div>

                            <div className="space-y-3">
                                <FormInput control={form.control} name="startDate" label="Start Date" type="date" />

                                <FormField
                                    control={form.control}
                                    name="repeatIncome"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between space-y-0">
                                            <FormLabel>Repeat income</FormLabel>
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={handleRepeatIncomeChange}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                {repeatIncome && (
                                    <>
                                        <FormSelect
                                            control={form.control}
                                            name="frequencyType"
                                            label="Frequency"
                                            placeholder="Select frequency"
                                        >
                                            {Object.values(FrequencyType)
                                                .filter(
                                                    (type) =>
                                                        type !== FrequencyType.ONE_TIME &&
                                                        type !== FrequencyType.DAILY &&
                                                        type !== FrequencyType.SEMI_MONTHLY,
                                                )
                                                .map((type) => (
                                                    <SelectItem key={type} value={type}>
                                                        {titleCase(type)}
                                                    </SelectItem>
                                                ))}
                                        </FormSelect>

                                        <FormSelect
                                            control={form.control}
                                            name="repeatUntil"
                                            label="Until"
                                            placeholder="Select until option"
                                        >
                                            <SelectItem value="cancel">I cancel</SelectItem>
                                            <SelectItem value="specific-date">Specific end date</SelectItem>
                                            <SelectItem value="occurrences">Number of occurrences</SelectItem>
                                        </FormSelect>

                                        {repeatUntil === 'specific-date' && (
                                            <FormInput
                                                control={form.control}
                                                name="endDate"
                                                label="End Date"
                                                type="date"
                                            />
                                        )}

                                        {repeatUntil === 'occurrences' && (
                                            <FormInput
                                                control={form.control}
                                                name="occurrenceCount"
                                                label="Number of Occurrences"
                                                type="number"
                                                placeholder="Enter number of occurrences"
                                            >
                                                {(field) => (
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        placeholder="Enter number of occurrences"
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                                    />
                                                )}
                                            </FormInput>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="space-y-3">
                                <FormCheckbox
                                    control={form.control}
                                    name="inflationAdjusted"
                                    label="Adjust for inflation"
                                />

                                {inflationAdjusted && (
                                    <FormInput
                                        control={form.control}
                                        name="inflationPercent"
                                        label="Inflation Rate (%)"
                                        placeholder="Enter inflation rate"
                                    >
                                        {(field) => (
                                            <NumberInput
                                                placeholder="Enter inflation rate"
                                                {...field}
                                                suffix="%"
                                                onChange={(e) => field.onChange(Number(e.target.value.split('%')[0]))}
                                            />
                                        )}
                                    </FormInput>
                                )}
                            </div>
                        </form>
                    </Form>
                </div>
                <DialogFooter>
                    <Button variant="outline" type="button" onClick={onCancel} onMouseDown={(e) => e.preventDefault()}>
                        Cancel
                    </Button>
                    <Button variant="default" type="submit" form="taxable-income-form">
                        {isAdding ? 'Add Income' : 'Save Changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
